// src/context/AuthContext.jsx
// Único punto de verdad para el estado de autenticación.
// REGLA: sessionStorage.getItem('user') y sessionStorage.getItem('token') solo deben aparecer AQUÍ y en src/api/client.js.

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { profileApi } from '../features/profile/api/profile.api';
import { get } from 'react-hook-form';
// Contexto
export const AuthContext = createContext(null);

// Provider
export function AuthProvider({ children }) {
  // Inicializar desde sessionStorage al montar (preserva sesión entre recargas)
  const [user, setUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });


  // login: persiste token y user en sessionStorage + actualiza estado
  const login = useCallback((userData, token) => {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);


  // logout: limpia todo
  const logout = useCallback(() => {
    sessionStorage.clear();
    setUser(null);
  }, []);

  // updateUser: para refrescar privilegios sin re-login
  const updateUser = useCallback((updatedUserData) => {
    sessionStorage.setItem('user', JSON.stringify(updatedUserData));
    setUser(updatedUserData);
    // Mantener compatibilidad con el evento que ya usa usePrivileges.js
    window.dispatchEvent(new Event('privilegesUpdated'));
  }, []);

  // Derivados: rol y privilegios
  const role = user?.role || '';
  const privileges = user?.privileges || [];
  const hasPrivilege = useCallback(  
    (priv) => role === 'SUPERADMIN' || privileges.includes(priv),
    [role, privileges]
  );

  const  areYouAllowed=(allowedPermissions)=>{
    const permit=privileges.some(item=>allowedPermissions.includes(item))
    return permit
  }
  const isCiudadano = role === 'CIUDADANO';
  const isFuncionario = role === 'FUNCIONARIO';
  const isSuperAdmin = role === 'SUPERADMIN';
  const isAuthenticated = !!user;




  const getMe = useCallback(async () => {
    try {
      if (!user?.username) return;
      const response = await profileApi.getMe();
        updateUser({
        fullName:response.name + ' '+ response.firstLastName??'' +' '+response.secondLastName??'',
        role:response.role,
        username:response.username,
        privileges:response.privileges
        });
    } catch (e) {
      console.error('Failed to fetch privileges', e);
    }
  }, [user, updateUser]);


  // Memoizar el valor del contexto para evitar re-renders innecesarios
  const value = useMemo(
    () => ({
      user,
      role,
      privileges,
      isAuthenticated,
      isCiudadano,
      isFuncionario,
      isSuperAdmin,
      hasPrivilege,
      areYouAllowed,
      login,
      logout,
      updateUser,
      getMe
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, hasPrivilege, login, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook de consumo — lanza error si se usa fuera del provider
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}
